import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PagePanel, Box, Text, Link } from 'edenred-ui';
import { useParams } from 'react-router-dom';

import { InfoBar } from '@epi-shared/components';
import { IBenefits } from '@epi-models/containers/ManageBenefits';

import { getDailyLimitSelectFieldOptions } from './helpers';
import {
  AddBenefitGroupTile,
  BenefitGroupDetail,
  CreatedGroupNotifications,
  BenefitGroupTiles
} from './components';
import {
  useGetAmounts,
  useManageBenefitGroups,
  useManageBenefitGroupsModal
} from './hooks';

export const ManageBenefitGroups = () => {
  const { t } = useTranslation();
  const { benefitGroup } = useParams<{ benefitGroup: IBenefits }>();
  const {
    benefitGroups,
    isBenefitGroupsLoaded,
    isBenefitGroupsError,
    isMassageOrWellbeing,
    isBenefitAvailable,
    shouldShowAddBenefitTile
  } = useManageBenefitGroups();
  const {
    detail,
    setDetail,
    handleViewDetail,
    handleCloseDetail,
    handleEditDetail,
    handleUpdateBenefitGroup,
    handleOpenCreateDatail,
    handleCreateBenefitGroup,
    handleCancelDetail
  } = useManageBenefitGroupsModal();
  const amount = useGetAmounts();

  const currentBenefitGroup = useMemo(
    () => benefitGroups.find(group => group.groupId === detail.currentGroupId),
    [detail.currentGroupId, benefitGroups]
  );

  const dailyLimitOptionsIncludingCurrentlySelected = useMemo(() => {
    return getDailyLimitSelectFieldOptions(
      amount,
      currentBenefitGroup?.maxAmountPerPeriod
    );
  }, [currentBenefitGroup]);

  return (
    <PagePanel
      title={t(`components.manage_benefits.new.benefits.${benefitGroup}`)}
      backIcon
      backIconLink={t('components.manage_benefit_lunch.back_link')}
    >
      {(detail.createdGroupSuggestion || detail.createdGroupNotification) && (
        <CreatedGroupNotifications
          detail={detail}
          isBenefitGroupsError={isBenefitGroupsError}
          setDetail={setDetail}
        />
      )}
      {isMassageOrWellbeing && (
        <Box mb={3}>
          <InfoBar
            node={
              <>
                <Box mb={1}>
                  <Text id="benefit-group-details-daily-limit-statement-text">
                    {t(
                      `components.manage_benefit_${benefitGroup}.mst_description`
                    )}
                  </Text>
                </Box>
                <Box mb={1}>
                  <Text id="benefit-group-details-daily-limit-statement-text">
                    <Trans
                      i18nKey={`components.manage_benefit_${benefitGroup}.mst_instructions`}
                      components={{
                        a: (
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            href={t(
                              `components.manage_benefit_${benefitGroup}.mst_instructions_link`
                            )}
                          />
                        )
                      }}
                    />
                  </Text>
                </Box>
                <Box>
                  <Text id="benefit-group-details-daily-limit-statement-text">
                    <Trans
                      i18nKey={`components.manage_benefit_${benefitGroup}.mst_cta`}
                      components={{
                        a: (
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            href={t(
                              `components.manage_benefit_${benefitGroup}.mst_cta_link`
                            )}
                          />
                        )
                      }}
                    />
                  </Text>
                </Box>
              </>
            }
          />
        </Box>
      )}
      {!(detail.createdGroupNotification || detail.createdGroupSuggestion) && (
        <Box maxWidth={860} pb={5}>
          <Text light>{t('components.manage_benefit_lunch.subtitle')}</Text>
        </Box>
      )}
      {isBenefitGroupsLoaded && (
        <Box
          display="flex"
          flexWrap="wrap"
          id={`employee-benefits-${benefitGroup}-group-tiles`}
        >
          <BenefitGroupTiles
            benefitGroups={benefitGroups}
            handleViewDetail={handleViewDetail}
            handleEditDetail={handleEditDetail}
          />
          {shouldShowAddBenefitTile && (
            <AddBenefitGroupTile onClick={handleOpenCreateDatail} />
          )}
        </Box>
      )}
      {isBenefitAvailable && (
        <BenefitGroupDetail
          detail={detail}
          onClose={handleCloseDetail}
          onSave={handleUpdateBenefitGroup}
          onCancel={handleCancelDetail}
          onCreate={handleCreateBenefitGroup}
          onEdit={handleEditDetail}
          benefitGroup={currentBenefitGroup}
          benefitType={benefitGroup}
          dailyLimitOptions={dailyLimitOptionsIncludingCurrentlySelected}
          key={currentBenefitGroup?.groupId}
        />
      )}
    </PagePanel>
  );
};
